import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { Arrow } from "@/components/icons";
import { site, process as steps } from "@/content";
import type { DetailContent } from "@/content/details";
import { Aurora, Grain, Particles, TiltCard, spotlightPointer } from "@/components/motion";

type Related = { label: string; href: string };

export function DetailPage(props: {
  eyebrow: string;
  heading: string;
  description: string;
  content: DetailContent;
  related?: Related[];
  ctaHref?: string;
  ctaLabel?: string;
}) {
  const ctaHref = props.ctaHref ?? site.primaryCta.href;
  const ctaLabel = props.ctaLabel ?? site.primaryCta.label;
  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <h1 className="hero-enter mt-4 max-w-3xl font-serif text-4xl leading-[1.1] text-ink sm:text-5xl">
            {props.heading}
          </h1>
          <p className="hero-enter-delay mt-6 max-w-2xl text-lg leading-relaxed text-ink/80">
            {props.description}
          </p>
          <div className="hero-enter-delay mt-8">
            <Link to={ctaHref} className="btn-primary">
              {ctaLabel}
              <Arrow className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Fit */}
      <section className="horizon-field--light border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="reveal font-serif text-2xl text-ink sm:text-3xl">
            This is likely a fit if…
          </h2>
          <ul className="stagger mt-6 grid gap-3 sm:grid-cols-2">
            {props.content.fit.map((f) => (
              <li key={f} className="flex items-start gap-2 leading-relaxed text-ink/80">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Included */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <h2 className="reveal mt-4 font-serif text-2xl text-ink sm:text-3xl">
            Exactly what you get. Not a vague scope.
          </h2>
          <div className="stagger mt-10 grid gap-6 md:grid-cols-2">
            {props.content.included.map((item) => (
              <TiltCard key={item.title} className="card-premium p-6">
                <h3 className="font-serif text-lg text-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.description}</p>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes: numbered, not another checklist — this is the second
          list on the page, so it needs its own shape rather than repeating
          the Fit section's checkmarks. */}
      <section className="horizon-field--light border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="reveal font-serif text-2xl text-ink sm:text-3xl">What good looks like</h2>
          <ol className="stagger mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2">
            {props.content.outcomes.map((o, idx) => (
              <li key={o} className="flex gap-4">
                <span className="font-serif text-2xl text-ink/25">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <p className="leading-relaxed text-ink/80">{o}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Process reminder */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="reveal mt-4 font-serif text-2xl text-ink sm:text-3xl">
            From where you are now to a monthly rhythm that just works.
          </h2>
          <ol className="stagger mt-8 grid gap-4 md:grid-cols-4">
            {steps.map((s) => (
              <li key={s.step}>
                <TiltCard className="card-premium h-full p-5">
                  <span className="font-serif text-2xl text-teal">
                    {String(s.step).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-serif text-base text-ink">{s.title}</h3>
                </TiltCard>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Related */}
      {props.related && props.related.length > 0 && (
        <section className="horizon-field--light border-b border-line">
          <div className="mx-auto max-w-5xl px-6 py-16">
            <h2 className="reveal mt-4 font-serif text-2xl text-ink sm:text-3xl">
              Worth a look too
            </h2>
            <div className="stagger mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {props.related.map((r) => (
                <TiltCard key={r.href}>
                  <Link
                    to={r.href}
                    className="link-underline card-premium block px-5 py-4 text-sm font-medium text-ink"
                  >
                    {r.label}
                  </Link>
                </TiltCard>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section onMouseMove={spotlightPointer} className="spotlight horizon-field text-ivory">
        <Aurora />
        <Grain />
        <Particles count={10} />
        <div className="relative z-10 mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="reveal text-shimmer font-serif text-2xl sm:text-3xl">
            Ready to find out what level of support actually fits?
          </h2>
          <p className="reveal mx-auto mt-3 max-w-2xl text-ivory/80">
            One conversation with Jamee, CPA. No pressure, no jargon.
          </p>
          <div className="reveal mt-6">
            <Link to={ctaHref} className="btn-on-dark">
              {ctaLabel}
              <Arrow className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
